import { isHorizontal } from './labelSizeConfig';
import { computeMetrics, buildLabelCss, buildGridPrintCss, extractSvgs, wrapInHtmlDocument } from './labelPrintHelpers';
import { buildDynamicBody } from './labelBodyBuilders';
import type { BuildLabelHtmlParams, BuildMultiLabelHtmlParams } from './labelPrintTypes';
import { GenericConstant } from '../../utils/GenericConstant';

export type { MultiLabelItem } from './labelPrintTypes';

export const buildLabelPrintHtml = ({
    qrFields,
    textFields,
    qrSvgHtml,
    size,
    orientation,
    isMaterialMode = false,
}: BuildLabelHtmlParams): string => {
    const m = computeMetrics(size, orientation, isMaterialMode, true);
    const horizontal = isHorizontal(orientation);
    const svgs = extractSvgs(qrSvgHtml);
    const body = buildDynamicBody(qrFields, textFields, svgs, m.qrMm, horizontal, isMaterialMode);
    const css = buildLabelCss(m, horizontal, '', false, isMaterialMode, true);

    return wrapInHtmlDocument(
        'Print SP Lot Label',
        css,
        `<div class="label-container">\n        ${body}\n    </div>`,
    );
};

export const buildMultiLabelPrintHtml = ({
    labels,
    size,
    orientation,
    isMaterialMode = false,
    copiesPerPage: copiesPerPageParam,
    leftMarginMm = 0,
}: BuildMultiLabelHtmlParams): string => {
    // Multiple copies only for SPLot labels, Material labels print single copy
    // Use API default_count if provided, otherwise fall back to constant
    const copiesPerPage = isMaterialMode ? 1 : (copiesPerPageParam ?? GenericConstant.LABELS_PER_PRINT_PAGE ?? 6);
    let gapMm = GenericConstant.LABEL_GRID_GAP_MM ?? 2;
    
    // For 6-label print layout: optimize for slip-on-tag paper (238×102 mm)
    // Page = 238 × 102 mm, divided vertically into 6 equal slots.
    // Layout math: 6 × 16mm labels + 5 × 1.0mm gaps = 96 + 5 = 101mm
    // 16mm (not 17) leaves printer-friendly breathing room so the first label is
    // not clipped at the top edge while still filling the page exactly.
    let printSize = size;
    let is6LabelLayout = false;
    if (copiesPerPage === 6 && !isMaterialMode) {
        // Override size for print: use optimal dimensions for 6-label vertical stack
        // This increases QR and text sizes automatically via computeMetrics
        printSize = '238x16';
        // Small gap between labels separates each slip-on tag slot cleanly
        gapMm = 1.0;
        is6LabelLayout = true;
    }

    // Requested print tuning: reduce SPLot inter-label spacing by 1mm.
    if (!isMaterialMode) {
        gapMm = Math.max(0, gapMm - 1);
    }

    // Keep slip-on-tag print behavior deterministic regardless template orientation.
    // For 6-label SPLot layout always force horizontal; material vertical stays vertical.
    const effectiveOrientation = is6LabelLayout ? 'Horizontal' : orientation;
    const m = computeMetrics(printSize, effectiveOrientation, isMaterialMode, true);
    const horizontal = isHorizontal(effectiveOrientation);
    
    const labelW = m.widthMm;
    const labelH = m.heightMm;
    
    // For 6-label layout: 1 column × 6 rows (vertically stacked, full page width)
    const gridColumns = copiesPerPage === 6 ? 1 : 1;
    const gridRows = copiesPerPage === 6 ? 6 : copiesPerPage;
    
    // The leftMarginMm is INCLUSIVE in the label width — it offsets content within
    // the label, so the printed page width must equal the label width (not extend
    // beyond it). The offset is applied as left padding inside the label container.
    const pageWidth = is6LabelLayout ? 238 : (labelW * gridColumns);
    const pageHeight = is6LabelLayout ? 102 : (labelH * gridRows + (gridRows - 1) * gapMm);
    
    const css = buildGridPrintCss(m, horizontal, gapMm, pageWidth, pageHeight, isMaterialMode, Math.max(0, leftMarginMm), true, gridColumns, gridRows, is6LabelLayout);

    // Build HTML for each unique label, then replicate for print (SPLot only)
    const labelDivs = labels.flatMap((item) => {
        const svgs = extractSvgs(item.qrSvgHtml);
        const body = buildDynamicBody(item.qrFields, item.textFields, svgs, m.qrMm, horizontal, isMaterialMode);
        // Create copies for SPLot, single for Material
        return Array(copiesPerPage).fill(`<div class="label-container">${body}</div>`);
    });
    
    // Group labels into pages (each page has copiesPerPage labels)
    const pages: string[] = [];
    for (let i = 0; i < labelDivs.length; i += copiesPerPage) {
        const pageLabels = labelDivs.slice(i, i + copiesPerPage);
        pages.push(`<div class="print-page">\n${pageLabels.join('\n')}\n</div>`);
    }

    return wrapInHtmlDocument('Print SP Lot Labels', css, pages.join('\n'));
};
